import {
  DeviceType,
  MetricField,
  MetricUnit,
  UpdateUserPreferencesPreferredUnitsConfigurationInput,
  UserPreferencesPreferredUnitsConfiguration,
} from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { getUnitsPerMetric } from '@/utils/getUnitsPerMetric';
import { getDeviceTypeOptions } from '@/utils/i18n/getDeviceTypeOptions';
import { getMetricDisplayName } from '@/utils/i18n/getMetricDisplayName';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { NumericTextFieldController } from '@components/controllers/NumericTextFieldController';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { AddCircle, Close } from '@mui/icons-material';
import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { UPDATE_USER_PREFERRED_UNITS } from '../graphql/mutations/updatePreferredUnits';
import { GET_CONFIGURABLE_FIELDS_FOR_PREFERRED_UNITS } from '../graphql/queries/getConfigurableMetrics';
import { PreferenceEntity } from '../types';
import { CustomModalActions } from './common/CustomModalActions';

interface Props {
  entity: PreferenceEntity;
  data: UserPreferencesPreferredUnitsConfiguration[];
  factoryDefaults: UserPreferencesPreferredUnitsConfiguration[];
}

interface FieldValues {
  groups: {
    groupName: MetricField;
    configurations: {
      deviceType: DeviceType | null;
      unit: MetricUnit;
      precision: number;
    }[];
  }[];
}

const deviceTypeOptions = getDeviceTypeOptions();

const getDefaultValues = (
  data: UserPreferencesPreferredUnitsConfiguration[],
  configurableMetricsGroups: { groupName: MetricField; affectedMetrics: Array<MetricField> }[],
): FieldValues => {
  return {
    groups: configurableMetricsGroups.map((metricGroup) => {
      const configurations = data
        .filter((configuration) => configuration.name === metricGroup.groupName)
        .map((configuration) => ({
          deviceType: configuration.deviceType ?? null,
          unit: configuration.unit,
          precision: configuration.precision,
        }))
        .sort((b, a) => Number(a.deviceType === null) - Number(b.deviceType === null));

      return {
        groupName: metricGroup.groupName,
        configurations: configurations,
      };
    }),
  };
};

const fieldValuesToConfigurationsInput = (
  data: FieldValues,
): UpdateUserPreferencesPreferredUnitsConfigurationInput[] => {
  return data.groups.flatMap((group) => {
    return group.configurations.map((configuration) => {
      return {
        deviceType: configuration.deviceType,
        name: group.groupName,
        unit: configuration.unit,
        precision: configuration.precision,
      };
    });
  });
};

export const UpdatePreferredUnitsForm: FC<Props> = ({ entity, data, factoryDefaults }) => {
  const {
    data: { configurableFieldsForPreferredUnits: metricGroups },
  } = useSuspenseQuery(GET_CONFIGURABLE_FIELDS_FOR_PREFERRED_UNITS);
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const { _ } = useLingui();
  const { control, handleSubmit, reset, formState } = useForm<FieldValues>({
    defaultValues: getDefaultValues(data, metricGroups),
  });

  const [mutateUser] = useMutation(UPDATE_USER_PREFERRED_UNITS, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Se han actualizado las preferencias`));
    },
    onError(error) {
      console.log(error.message);
      openModalErrorMessage(_(msg`Ha ocurrido un error al actualizar`));
    },
    update(cache) {
      Object.keys(cache.extract()).forEach((key) => {
        if (key.startsWith('Metrics:')) {
          cache.evict({ id: key });
        }
      });

      cache.gc();
    },
  });

  useEffect(() => {
    reset(getDefaultValues(data, metricGroups));
  }, [data, reset, metricGroups]);

  const onSubmit = handleSubmit(async (data) => {
    const isFactory =
      JSON.stringify(data) === JSON.stringify(getDefaultValues(factoryDefaults, metricGroups));
    const configurations = isFactory ? null : fieldValuesToConfigurationsInput(data);

    if (entity.type === 'user') {
      await mutateUser({
        variables: {
          input: {
            userId: entity.id,
            configurations,
          },
        },
      });
    } else if (entity.type === 'organization') {
      openModalErrorMessage('Not implemented');
    }
  });

  const factoryDefaultsHandler = () => {
    reset(getDefaultValues(factoryDefaults, metricGroups), { keepDefaultValues: true });
  };

  const resetHandler = () => {
    reset();
  };

  const { isDirty, isSubmitting } = formState;

  return (
    <form onSubmit={onSubmit} style={{ height: '100%' }}>
      <Box
        height="100%"
        gap={2}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box overflow="auto">
          <Typography variant="h5" mb={2}>
            <Trans>Unidad de medida y precisión</Trans>
          </Typography>
          {metricGroups.map((metricGroup, index) => (
            <MetricGroupConfigurations
              key={metricGroup.groupName}
              control={control}
              groupIndex={index}
              metricGroup={metricGroup}
            />
          ))}
        </Box>
        <CustomModalActions
          factoryDefaultsHandler={factoryDefaultsHandler}
          resetHandler={resetHandler}
          isDirty={isDirty}
          isSubmitting={isSubmitting}
        />
      </Box>
    </form>
  );
};

interface MetricGroupConfigurationsProps {
  metricGroup: {
    groupName: MetricField;
    affectedMetrics: Array<MetricField>;
  };
  control: Control<FieldValues>;
  groupIndex: number;
}

const MetricGroupConfigurations: FC<MetricGroupConfigurationsProps> = ({
  metricGroup,
  control,
  groupIndex,
}) => {
  const { i18n } = useLingui();
  const metricName = getMetricDisplayName(metricGroup.groupName, i18n);
  const unitOptions = getUnitsPerMetric(metricGroup.groupName, i18n);

  const { fields, append, remove } = useFieldArray({
    control,
    name: `groups.${groupIndex}.configurations`,
  });

  const onAddField = () => {
    append({
      deviceType: DeviceType.T,
      unit: unitOptions[0].enumValue,
      precision: 0,
    });
  };

  const onRemoveField = (index: number) => {
    remove(index);
  };

  return (
    <Box my={2}>
      <Typography variant="h6">{metricName}</Typography>

      {fields.map((field, configurationIndex) => {
        return (
          <MetricGroupConfigurationField
            key={field.id}
            control={control}
            groupIndex={groupIndex}
            configurationIndex={configurationIndex}
            onRemoveField={onRemoveField}
            unitOptions={unitOptions}
          />
        );
      })}

      <Button
        onClick={onAddField}
        size="small"
        startIcon={<AddCircle />}
        sx={{ mt: 1 }}
        color="info"
      >
        <Trans>Configurar otro tipo de dispositivo</Trans>
      </Button>
    </Box>
  );
};

interface MetricGroupConfigurationFieldProps {
  control: Control<FieldValues>;
  groupIndex: number;
  configurationIndex: number;
  onRemoveField: (index: number) => void;
  unitOptions: {
    value: string;
    enumValue: MetricUnit;
    label: string;
  }[];
}

const MetricGroupConfigurationField: FC<MetricGroupConfigurationFieldProps> = ({
  control,
  groupIndex,
  configurationIndex,
  onRemoveField,
  unitOptions,
}) => {
  const deviceType = useWatch({
    control,
    name: `groups.${groupIndex}.configurations.${configurationIndex}.deviceType`,
  });

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 40px" gap={1} mt={1} alignItems="center">
      <Controller
        control={control}
        name={`groups.${groupIndex}.configurations.${configurationIndex}.deviceType`}
        render={({ field: { value, ...field }, fieldState: { error } }) =>
          value == null ? (
            <Typography color="text.disabled" fontStyle="italic" px={1.5}>
              <Trans>Por defecto</Trans>
            </Typography>
          ) : (
            <Autocomplete
              {...field}
              value={value}
              onChange={(_, newValue) => {
                field.onChange(newValue);
              }}
              fullWidth
              disableClearable
              options={deviceTypeOptions.map((option) => option.enumValue)}
              getOptionLabel={(option) =>
                deviceTypeOptions.find((deviceType) => deviceType.enumValue === option)?.label ??
                option
              }
              size="small"
              renderInput={(params) => (
                <TextField
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={<Trans>Tipo de dispositivo</Trans>}
                  {...params}
                />
              )}
            />
          )
        }
      />
      <Controller
        control={control}
        name={`groups.${groupIndex}.configurations.${configurationIndex}.unit`}
        render={({ field }) => (
          <Autocomplete
            {...field}
            onChange={(_, newValue) => {
              field.onChange(newValue);
            }}
            disableClearable
            fullWidth
            options={unitOptions.map((option) => option.enumValue)}
            getOptionLabel={(option) =>
              unitOptions.find((unit) => unit.enumValue === option)?.label ?? option
            }
            size="small"
            renderInput={(params) => (
              <TextField label={<Trans>Unidad de medida</Trans>} {...params} />
            )}
          />
        )}
      />
      <NumericTextFieldController
        control={control}
        name={`groups.${groupIndex}.configurations.${configurationIndex}.precision`}
        label={<Trans>Decimales de precisión</Trans>}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
          },
        }}
      />

      {deviceType != null && (
        <IconButton onClick={() => deviceType != null && onRemoveField(configurationIndex)}>
          <Close />
        </IconButton>
      )}
    </Box>
  );
};
